export type Language = "en" | "fi" | "sv";

export const LANGUAGES: { code: Language; name: string; flag: string }[] = [
  { code: "en", name: "English", flag: "GB" },
  { code: "fi", name: "Suomi", flag: "FI" },
  { code: "sv", name: "Svenska", flag: "SE" },
];

export const translations = {
  en: {
    // Header
    appTitle: "Coffee Calculator",

    // Hero
    heroLine1: "Calculate the perfect amount of coffee for your brew.",
    heroLine2: "Enter either coffee or water, and the other will be calculated.",

    // Calculator
    coffee: "COFFEE",
    water: "WATER",
    grams: "grams",
    milliliters: "milliliters",
    brewingRatio: "Brewing Ratio",
    lighter: "Lighter",
    stronger: "Stronger",
    perLiter: "per liter",

    // Results
    cups: "cups",
    cupSize: "200ml",
    ratio: "ratio",

    // Roast level
    roastLevel: "Roast Level",
    roastLight: "Light",
    roastMedium: "Medium",
    roastDark: "Dark",

    // Tip
    tipTitle: "Nordic Style:",
    tipText: "The default ratio of 60g per liter creates a balanced, medium-strength brew typical of Nordic coffee culture. Adjust the slider to your taste!",

    // Footer
    footerText: "Swedish/Finnish style brewing ratio",

    // Accessibility
    switchTheme: "Switch to {theme} mode",
    light: "light",
    dark: "dark",
    selectLanguage: "Select language",
  },
  fi: {
    // Header
    appTitle: "Kahvilaskuri",

    // Hero
    heroLine1: "Laske täydellinen kahvimäärä keittämistä varten.",
    heroLine2: "Syötä joko kahvin tai veden määrä, niin toinen lasketaan automaattisesti.",

    // Calculator
    coffee: "KAHVI",
    water: "VESI",
    grams: "grammaa",
    milliliters: "millilitraa",
    brewingRatio: "Keittosuhde",
    lighter: "Miedompi",
    stronger: "Vahvempi",
    perLiter: "per litra",

    // Results
    cups: "kuppia",
    cupSize: "200ml",
    ratio: "suhde",

    // Roast level
    roastLevel: "Paahtoaste",
    roastLight: "Vaalea",
    roastMedium: "Keski",
    roastDark: "Tumma",

    // Tip
    tipTitle: "Pohjoismainen tyyli:",
    tipText: "Oletussuhde 60g litraa kohti tuottaa tasapainoisen, keskivahvan kahvin, joka on tyypillinen pohjoismaiselle kahvikulttuurille. Säädä liukusäädintä makusi mukaan!",

    // Footer
    footerText: "Suomalais-ruotsalainen keittosuhde",

    // Accessibility
    switchTheme: "Vaihda {theme}-tilaan",
    light: "vaalea",
    dark: "tumma",
    selectLanguage: "Valitse kieli",
  },
  sv: {
    // Header
    appTitle: "Kaffekalkylator",

    // Hero
    heroLine1: "Beräkna den perfekta mängden kaffe för din bryggning.",
    heroLine2: "Ange antingen kaffe eller vatten, så beräknas det andra automatiskt.",

    // Calculator
    coffee: "KAFFE",
    water: "VATTEN",
    grams: "gram",
    milliliters: "milliliter",
    brewingRatio: "Bryggförhållande",
    lighter: "Mildare",
    stronger: "Starkare",
    perLiter: "per liter",

    // Results
    cups: "koppar",
    cupSize: "200ml",
    ratio: "förhållande",

    // Roast level
    roastLevel: "Rostgrad",
    roastLight: "Ljus",
    roastMedium: "Mellan",
    roastDark: "Mörk",

    // Tip
    tipTitle: "Nordisk stil:",
    tipText: "Standardförhållandet 60g per liter ger en balanserad, mediumstark bryggning typisk för nordisk kaffekultur. Justera reglaget efter din smak!",

    // Footer
    footerText: "Svensk/finsk bryggförhållande",

    // Accessibility
    switchTheme: "Byt till {theme}-läge",
    light: "ljust",
    dark: "mörkt",
    selectLanguage: "Välj språk",
  },
} as const;

export type TranslationKey = keyof typeof translations.en;

export function getTranslation(lang: Language, key: TranslationKey): string {
  return translations[lang][key];
}

export function detectLanguage(): Language {
  if (typeof window === "undefined") return "en";

  const browserLang = navigator.language.split("-")[0].toLowerCase();

  if (browserLang === "fi") return "fi";
  if (browserLang === "sv") return "sv";
  return "en";
}
